import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dimension, EstadoExportacion, Exportacion, ListaExportaciones, ModoComparacion } from '../../api/ops/indicadores';
import { claveIdempotencia } from '../../api/operaciones';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Fecha } from '../../componentes/Fecha';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { Paginacion } from '../../componentes/Paginacion';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Tabla } from '../../componentes/Tabla';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { diaYHora, hoyLocal } from '../../logica/fechasCortas';
import { navegador } from '../../logica/navegador';
import { haySiguiente, usePaginacionCursor } from '../../logica/paginacion';
import { useCabecera } from '../../marco/cabecera';
import type { PropsApartado } from './Indicadores';
import {
    AGRUPACIONES,
    agruparPorArea,
    AYUDA_EXPORTACIONES,
    SUBTITULO_INDICADORES,
    COMPARACIONES,
    cuerpoExportacion,
    desglosesComunes,
    detalleExportacion,
    erroresExportacion,
    estadoExportacion,
    FILTROS_EXPORTACION,
    formularioDe,
    formularioInicial,
    MAX_INDICADORES,
    nombreDimension,
    PERIODOS,
    textoPeriodoExportacion,
    tituloExportacion,
    type FormExportacion,
    type Periodo,
} from './reglas';

/**
 * 7.3 «Exportaciones» (solo con `analitica.indicadores.exportar`): las que ha pedido quien mira (`listAnalyticsExports`,
 * filtro por estado, «Anterior / Siguiente»), «Descargar» con el enlace firmado de `getAnalyticsExport` y «Pedir otra
 * vez» en las caducadas y las fallidas. «Nueva exportación» (RV Panel): hasta 20 indicadores de los que puede leer,
 * periodo hasta 25 meses, agrupación, sede, desglose, comparación y CSV o Excel, con `createAnalyticsExport` e
 * `Idempotency-Key` (la misma al reintentar). Las grandes se preparan y avisan en la campana (G429), cuyo enlace abre la
 * lista con la fila de `?exportacion={id}` resaltada (G888). Se puede exportar en solo lectura.
 */
export function Exportaciones(props: PropsApartado) {
    const { ops, negocio, datos, movil, busqueda, navegar, ruta } = props;
    const avisar = useAvisoBreve();
    const [estado, setEstado] = useState<EstadoExportacion | null>(null);
    const pag = usePaginacionCursor();
    const [lista, setLista] = useState<ListaExportaciones | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [form, setForm] = useState<FormExportacion | null>(null);
    const nueva = busqueda.get('nueva');
    // G888: el enlace de la campana (`analytics_export`, G823) abre la lista con la fila de `?exportacion=` resaltada.
    const resaltada = busqueda.get('exportacion');

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        let vivo = true;
        ops.listAnalyticsExports(negocio, { status: estado ?? undefined, cursor: pag.cursor })
            .then((l) => {
                if (vivo) {
                    setLista(l);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, estado, pag.cursor, intento]);

    // «Exportar» desde Indicadores o desde la ficha: `?nueva=1` o `?nueva=<código>` abre el formulario.
    const abierto = form ?? (nueva === null ? null : formularioInicial(datos.catalogo.some((i) => i.code === nueva) ? [nueva] : []));

    const cerrar = () => {
        setForm(null);
        if (nueva !== null) {
            const p = new URLSearchParams(busqueda);
            p.delete('nueva');
            navegar(`${ruta.split('?')[0] ?? ruta}?${p.toString()}`, true);
        }
    };

    useCabecera({ ayuda: AYUDA_EXPORTACIONES, subtitulo: SUBTITULO_INDICADORES, acciones: [{ clave: 'nueva', texto: 'Nueva exportación', icono: 'download', onClick: () => { setForm(formularioInicial()); } }] });

    const nombreDe = (codigo: string) => datos.catalogo.find((i) => i.code === codigo)?.name ?? codigo;
    const nombreSede = (id: string) => datos.sedes.find((s) => s.id === id)?.nombre ?? 'Una sede';

    const descargar = (x: Exportacion) => {
        ops.getAnalyticsExport(negocio, x.id)
            .then((y) => {
                if (y.status === 'ready' && y.download_url != null) {
                    navegador.abrir(y.download_url);
                } else {
                    avisar({ tipo: 'error', texto: 'El enlace ya no vale. Pídela otra vez.' });
                    recargar();
                }
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido descargar.', reintentar: () => { descargar(x); } });
            });
    };

    let contenido;
    if (error !== null) {
        contenido = <EstadoError seccion="tus exportaciones" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    } else if (lista === null) {
        contenido = <EstadoCargando seccion="tus exportaciones" />;
    } else {
        const filas = lista.data.map((x) => {
            const t = tituloExportacion(x, nombreDe);
            const e = estadoExportacion(x);
            const boton =
                x.status === 'ready' ? (
                    <Boton tipo="secundario" icono="download" movil={movil} onClick={() => { descargar(x); }}>
                        Descargar
                    </Boton>
                ) : x.status === 'expired' || x.status === 'failed' ? (
                    <Boton tipo="secundario" icono="refresh-cw" movil={movil} onClick={() => { setForm(formularioDe(x)); }}>
                        Pedir otra vez
                    </Boton>
                ) : undefined;
            return {
                clave: x.id,
                celdas: {
                    indicadores: (
                        <span className="rv-ind-fila">
                            <span className="rv-ca-baldosa">
                                <Icono nombre={x.format === 'csv' ? 'file-text' : 'file-spreadsheet'} />
                            </span>
                            <span className="rv-ca-fila__texto">
                                <strong>{t.titulo}</strong>
                                <span>{t.sub}</span>
                            </span>
                        </span>
                    ),
                    periodo: (
                        <span className="rv-ca-fila__texto">
                            <span className="rv-ind-negro">{textoPeriodoExportacion(x.from, x.to)}</span>
                            <span>{detalleExportacion(x, nombreSede)}</span>
                        </span>
                    ),
                    pedida: diaYHora(x.created_at),
                    estado: (
                        <span className="rv-ca-fila__texto">
                            <span>
                                <Chip estado={e.tono}>{e.chip}</Chip>
                            </span>
                            <span>{e.linea}</span>
                        </span>
                    ),
                },
                ...(boton === undefined ? {} : { acciones: boton }),
                ...(x.id === resaltada ? { resaltada: true } : {}),
            };
        });
        contenido = (
            <>
                <Tabla
                    etiqueta="Tus exportaciones"
                    movil={movil}
                    columnas={[
                        { clave: 'indicadores', titulo: 'Indicadores', ancho: 'minmax(0, 2fr)' },
                        { clave: 'periodo', titulo: 'Periodo', ancho: 'minmax(0, 1.4fr)' },
                        { clave: 'pedida', titulo: 'Pedida', ancho: 'minmax(0, 0.7fr)' },
                        { clave: 'estado', titulo: 'Estado', ancho: 'minmax(0, 1.1fr)' },
                    ]}
                    filas={filas}
                    vacia={
                        <div className="rv-ca-vacia">
                            <span className="rv-ca-vacia__baldosa">
                                <Icono nombre="download" />
                            </span>
                            <strong>{estado === null ? 'Aún no has pedido ninguna exportación.' : 'No hay exportaciones con este estado.'}</strong>
                            <span>{estado === null ? 'Con «Nueva exportación» eliges los indicadores, el periodo y el formato.' : 'Prueba con «Todas».'}</span>
                        </div>
                    }
                />
                {(pag.hayAnterior || haySiguiente(lista.next_cursor)) && (
                    <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
                )}
            </>
        );
    }

    return (
        <div className="rv-ind">
            <Filtros
                etiqueta="Estado de las exportaciones"
                items={FILTROS_EXPORTACION.map((f) => ({ etiqueta: f.texto }))}
                valor={Math.max(0, FILTROS_EXPORTACION.findIndex((f) => f.valor === estado))}
                onCambio={(i) => {
                    setEstado(FILTROS_EXPORTACION[i]?.valor ?? null);
                    pag.reiniciar();
                }}
            />
            {contenido}
            <NuevaExportacionPanel
                {...props}
                form={abierto}
                setForm={setForm}
                onCerrar={cerrar}
                onHecha={(x) => {
                    avisar({ tipo: 'ok', texto: x.status === 'ready' ? 'Exportación lista.' : 'Preparando la exportación. Te avisamos en la campana.' });
                    cerrar();
                    setEstado(null);
                    pag.reiniciar();
                    recargar();
                }}
            />
        </div>
    );
}

function NuevaExportacionPanel({ ops, negocio, datos, movil, form, setForm, onCerrar, onHecha }: PropsApartado & { form: FormExportacion | null; setForm: (f: FormExportacion | null) => void; onCerrar: () => void; onHecha: (x: Exportacion) => void }) {
    const avisar = useAvisoBreve();
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    // Una clave por formulario abierto: reintentar el mismo envío la repite; se olvida al cerrarlo.
    const clave = useRef<string | null>(null);
    const hoy = hoyLocal();
    const grupos = useMemo(() => agruparPorArea(datos.catalogo), [datos.catalogo]);
    const abierto = form !== null;
    const olvidar = () => {
        clave.current = null;
        setIntentado(false);
    };

    const f = form ?? formularioInicial();
    const errores = intentado ? erroresExportacion(f, hoy) : {};
    const elegidos = datos.catalogo.filter((i) => f.elegidos.includes(i.code));
    const desgloses = desglosesComunes(elegidos);
    const cambiar = (c: Partial<FormExportacion>) => {
        setForm({ ...f, ...c });
    };
    const marcar = (codigos: readonly string[], si: boolean) => {
        const resto = f.elegidos.filter((c) => !codigos.includes(c));
        const elegidosNuevos = si ? [...resto, ...codigos] : resto;
        const sigue = f.desglose === '' || desglosesComunes(datos.catalogo.filter((i) => elegidosNuevos.includes(i.code))).includes(f.desglose);
        cambiar({ elegidos: elegidosNuevos, ...(sigue ? {} : { desglose: '' }) });
    };
    const enviar = () => {
        setIntentado(true);
        if (Object.keys(erroresExportacion(f, hoy)).length > 0) {
            return;
        }
        setOcupado(true);
        clave.current ??= claveIdempotencia();
        ops.createAnalyticsExport(negocio, cuerpoExportacion(f, hoy), clave.current)
            .then((x) => {
                olvidar();
                onHecha(x);
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido pedir la exportación.', reintentar: enviar });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    return (
        <PanelLateral
            abierto={abierto}
            onCerrar={() => {
                olvidar();
                onCerrar();
            }}
            movil={movil}
            titulo="Nueva exportación"
            eyebrow="Exportar cifras"
            subtitulo="CSV o Excel. Solo cifras agregadas."
            icono="download"
            accion="Exportar"
            accionIcono="download"
            ocupado={ocupado}
            ocupadoTexto="Preparando…"
            onAccion={enviar}
        >
            {abierto && (
                <div className="rv-ind-form">
                    <div className="rv-ind-form__rejilla">
                        <Desplegable etiqueta="Periodo" valor={f.periodo} opciones={PERIODOS.map((p) => ({ valor: p.valor, texto: p.texto }))} onChange={(v) => { cambiar({ periodo: v as Periodo, ...(v === '12m' ? { agrupacion: 'month' as const } : {}) }); }} />
                        <Desplegable etiqueta="Agrupar por" valor={f.agrupacion} opciones={AGRUPACIONES.map((g) => ({ valor: g.valor, texto: g.texto }))} onChange={(v) => { cambiar({ agrupacion: v as FormExportacion['agrupacion'] }); }} />
                        {f.periodo === 'fechas' && (
                            <>
                                <Fecha etiqueta="Desde" valor={f.fechas.desde} max={hoy} onChange={(v) => { cambiar({ fechas: { ...f.fechas, desde: v } }); }} />
                                <Fecha etiqueta="Hasta" valor={f.fechas.hasta} max={hoy} error={errores.fechas ?? null} onChange={(v) => { cambiar({ fechas: { ...f.fechas, hasta: v } }); }} />
                            </>
                        )}
                        <Desplegable etiqueta="Sede" valor={f.sede} opciones={[{ valor: '', texto: 'Todas las sedes' }, ...datos.sedes.map((s) => ({ valor: s.id, texto: s.nombre }))]} onChange={(v) => { cambiar({ sede: v }); }} />
                        <Desplegable etiqueta="Comparar con" valor={f.comparar} opciones={COMPARACIONES.map((c) => ({ valor: c.valor, texto: c.texto }))} onChange={(v) => { cambiar({ comparar: v as ModoComparacion }); }} />
                        <Desplegable
                            etiqueta="Desglosar por"
                            valor={f.desglose}
                            bloqueado={desgloses.length === 0}
                            ayuda={desgloses.length === 0 ? 'Solo las dimensiones que tienen todos los indicadores elegidos.' : null}
                            opciones={[{ valor: '', texto: 'Sin desglose' }, ...desgloses.map((d) => ({ valor: d, texto: nombreDimension(d) }))]}
                            onChange={(v) => { cambiar({ desglose: v as Dimension | '' }); }}
                        />
                    </div>
                    <div>
                        <div className="rv-ind-ceja">Formato</div>
                        <Filtros
                            etiqueta="Formato"
                            items={[{ etiqueta: 'Excel' }, { etiqueta: 'CSV' }]}
                            valor={f.formato === 'csv' ? 1 : 0}
                            onCambio={(i) => { cambiar({ formato: i === 1 ? 'csv' : 'xlsx' }); }}
                        />
                    </div>
                    <div className="rv-ind-form__cuenta">
                        <strong>Indicadores</strong>
                        <span className={f.elegidos.length > MAX_INDICADORES ? 'rv-ind-rojo' : 'rv-ind-gris'}>{`${String(f.elegidos.length)} de ${String(MAX_INDICADORES)}`}</span>
                    </div>
                    {errores.elegidos !== undefined && (
                        <p className="rv-ind-rojo" role="alert">
                            {errores.elegidos}
                        </p>
                    )}
                    {grupos.map((g) => {
                        const codigos = g.indicadores.map((i) => i.code);
                        const todos = codigos.every((c) => f.elegidos.includes(c));
                        return (
                            <section key={g.area} className="rv-ind-form__grupo" aria-label={g.nombre}>
                                <div className="rv-ind-form__cuenta">
                                    <span className="rv-ind-ceja">
                                        <Icono nombre={g.icono} /> {g.nombre}
                                    </span>
                                    <button type="button" className="rv-ind-mas" onClick={() => { marcar(codigos, !todos); }}>
                                        {todos ? 'Quitar todos' : 'Todos'}
                                    </button>
                                </div>
                                {g.indicadores.map((i) => (
                                    <Interruptor
                                        key={i.code}
                                        etiqueta={i.name}
                                        ayuda={i.unit === 'money' ? 'Con importes' : i.unit === 'percent' ? 'Porcentaje, con numerador y denominador' : undefined}
                                        valor={f.elegidos.includes(i.code)}
                                        onChange={(v) => { marcar([i.code], v); }}
                                    />
                                ))}
                            </section>
                        );
                    })}
                </div>
            )}
        </PanelLateral>
    );
}
